import {
  Autocomplete,
  FormControl,
  GridLegacy as Grid,
  TextField,
} from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { EmployeeItemInfoData } from '~/components/GenericList/EmployeeItemInfo';
import { OrgUnitEmployeeRole } from '~/data/orgunits/types';
import { useHRResponsibleRolesWithIds } from '~/data/orgunits/useHRResponsibleRolesWithIds';
import { OrgUnitFormData } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/types';
import { isRoleHRAdmin, isRoleHRAdminLimited } from '~/services/Permissions';

type PropsType = {
  form: UseFormReturn<OrgUnitFormData>;
  hrResponsibles: EmployeeItemInfoData[];
  disabled?: boolean;
  commonTextFieldProps?: Partial<TextFieldProps>;
};

export const OrgUnitHrResponsibleField = (props: PropsType) => {
  const { form, hrResponsibles, disabled, commonTextFieldProps } = props;
  const { control } = form;

  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);
  const isHRLimited = isRoleHRAdminLimited(loggedInUserRoles);

  const canEditHRResponsibles = isHRAdmin || isHRLimited;

  const { t } = useTranslation();

  const { rolesWithIds } = useHRResponsibleRolesWithIds();

  const mapRole = (role: string | undefined) => {
    switch (role) {
      case 'HR_ADMIN':
        return OrgUnitEmployeeRole.HRAdmin;
      case 'HR_STAFF':
        return OrgUnitEmployeeRole.HRStaff;
      case 'HR_DEV':
        return OrgUnitEmployeeRole.HRDev;
      case 'HR_SALARY':
        return OrgUnitEmployeeRole.HRSalary;
      case 'HR_ADMIN_LIMITED':
        return OrgUnitEmployeeRole.HRLimited;
      case 'HR_AUDITOR':
        return OrgUnitEmployeeRole.HRAuditor;
      case 'HR_PEOP':
        return OrgUnitEmployeeRole.HRPeople;
      case 'HR_PEOP_DEV':
        return OrgUnitEmployeeRole.HRPeopleDev;
      default:
        return null;
    }
  };

  return (
    <Grid item xs={12}>
      <Controller
        control={control}
        name="responsibleHRs"
        render={({ field: { onChange, value, ref, ...field } }) => (
          <FormControl fullWidth>
            <Autocomplete
              {...field}
              multiple
              options={hrResponsibles}
              getOptionLabel={(hr) =>
                `${hr.employee?.fEmpFirstName} ${hr.employee?.fEmpLastName}, ${hr.employee?.fEmpPersonNumber}`
              }
              value={
                value
                  .map((hr) =>
                    hrResponsibles.find(
                      (hrResponsible) =>
                        hrResponsible.employeeId === hr.fEmployeeId,
                    ),
                  )
                  .filter((value) => !!value) as EmployeeItemInfoData[]
              }
              onChange={async (_, newValue) => {
                const newResponsibleHRs = newValue.map((hr) => {
                  const role = rolesWithIds?.find(({ ids }) =>
                    ids.includes(hr.employeeId),
                  )?.role;
                  return {
                    fEmployeeId: hr.employeeId,
                    fUnitRole: mapRole(role),
                  };
                });

                onChange(newResponsibleHRs);
              }}
              disabled={disabled || !canEditHRResponsibles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('fOrgUnitResponsibleHrEmps')}
                  inputRef={ref}
                  {...commonTextFieldProps}
                />
              )}
            />
          </FormControl>
        )}
      />
    </Grid>
  );
};
